import { fetchCart, syncCartNote } from '@theme/uradora-cart';

/**
 * Checkout for a cart holding pre-order boxes (snippets/uradora-checkout-form.liquid marks its
 * form with data-preorder-checkout). Instead of going straight to checkout, the cart's lines go
 * to the pre-order middleware through the store's app proxy. It checks them with Shopify, turns
 * the pre-order boxes into a draft order for the logged-in customer and names the lines to take
 * out of the cart. Once they're removed the customer lands on the My pre-orders page, which
 * offers checkout for anything left in the cart.
 *
 * While this runs, <html> carries data-preorder-sending, which brings up the cart's veil and
 * spinner over the drawer or cart page with a message under it
 * (snippets/uradora-cart-boxes.liquid), and the cart's status region announces it.
 *
 * The cart is sent from here because the middleware can't read it itself while the online
 * store is password protected. Sending the same cart again returns the same draft, so a retry
 * after a failed removal never makes a second one.
 *
 * Carts without pre-order boxes never reach this code and check out as usual.
 */

/** @type {WeakSet<HTMLFormElement>} */
const sending = new WeakSet();

document.addEventListener('submit', (event) => {
  const form = event.target;
  if (!(form instanceof HTMLFormElement) || !form.hasAttribute('data-preorder-checkout')) return;

  event.preventDefault();
  submitPreorder(form);
});

/** @param {HTMLFormElement} form */
async function submitPreorder(form) {
  if (form.dataset.loggedIn !== 'true') {
    window.location.assign(form.dataset.loginUrl ?? '/customer_authentication/login');
    return;
  }
  if (sending.has(form)) return;

  sending.add(form);
  const restore = showSending(form);

  try {
    const cart = await fetchCart();
    const response = await fetch(form.dataset.endpoint ?? '', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ cartToken: cart.token, lines: (cart.items ?? []).map(toLine) }),
    });
    const result = await response.json().catch(() => null);

    if (result?.error === 'login_required') {
      window.location.assign(form.dataset.loginUrl ?? '/customer_authentication/login');
      return;
    }
    if (!response.ok || !result?.ok) throw new Error(result?.error || `Pre-order request failed (${response.status})`);

    // Nothing in the cart was a pre-order after all (the collection changed), so check out as usual.
    if (!result.draft) {
      window.location.assign('/checkout');
      return;
    }

    /** @type {string[]} */
    const remove = result.remove ?? [];
    if (remove.length > 0) await removeLines(remove);

    // The note lists every box for the team; it should now list only what's going to checkout.
    await syncCartNote().catch((error) => console.error(error));

    // The veil stays up until the next page takes over.
    const next = new URL(form.dataset.successUrl || '/pages/pre-orders', window.location.origin);
    next.searchParams.set('submitted', result.draft.name);
    window.location.assign(next);
  } catch (error) {
    console.error(error);
    restore();
    sending.delete(form);

    const code = error instanceof Error ? error.message : '';
    showError(form, code === 'rate_limited' ? form.dataset.errorRateLimited : form.dataset.error);
  }
}

/**
 * Takes the pre-order boxes out of the cart, each with everything nested under it, in one
 * change. The cart isn't re-rendered: the page moves on next, and re-rendering a cart that only
 * held pre-orders would flash "Your cart is empty" under the veil first.
 * @param {string[]} keys - Line keys
 */
async function removeLines(keys) {
  const response = await fetch(`${Theme.routes.cart_update_url}.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ updates: Object.fromEntries(keys.map((key) => [key, 0])) }),
  });
  const result = await response.json().catch(() => null);
  if (!response.ok || !result || result.status) {
    throw new Error(result?.description || result?.message || 'Removing pre-order boxes failed');
  }
}

/**
 * A cart line as the middleware reads it.
 * @param {any} item - An AJAX cart item
 */
function toLine(item) {
  return {
    key: item.key,
    variantId: item.variant_id,
    quantity: item.quantity,
    properties: item.properties ?? {},
    parentKey: item.parent_relationship?.parent_key ?? null,
  };
}

/**
 * Brings up the veil and its message, and holds the button, until the page moves on.
 * @param {HTMLFormElement} form
 * @returns {() => void} Puts everything back
 */
function showSending(form) {
  const button = form.querySelector('button[name="checkout"]');
  const error = form.querySelector('[data-preorder-error]');
  if (error instanceof HTMLElement) error.hidden = true;

  document.documentElement.toggleAttribute('data-preorder-sending', true);
  announce(form.dataset.textBusy ?? '');

  if (button instanceof HTMLButtonElement) {
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
  }

  return () => {
    document.documentElement.toggleAttribute('data-preorder-sending', false);
    announce('');

    if (button instanceof HTMLButtonElement) {
      button.disabled = false;
      button.setAttribute('aria-busy', 'false');
    }
  };
}

/**
 * Says what's happening through the cart's status regions, since the veil is hidden from
 * screen readers.
 * @param {string} text
 */
function announce(text) {
  for (const status of document.querySelectorAll('[data-cart-status]')) status.textContent = text;
}

/**
 * @param {HTMLFormElement} form
 * @param {string} [message]
 */
function showError(form, message) {
  const error = form.querySelector('[data-preorder-error]');
  if (!(error instanceof HTMLElement)) return;
  error.textContent = message ?? '';
  error.hidden = false;
}
